import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withState, withHandlers, withProps } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { makeSentenceFragments, FragmentRenderer, renderFragment } from 'util/makeSentenceFragments';
import { Input } from 'ui/shared/Input';

type OwnProps = {
    id: string;
    value: number;
    onChange: (n: number) => void;
};

const RawDwellTime = ({ sentenceFragments, renderFragment }: RecomposeProps) =>
    <Fragment>
        { sentenceFragments.map(renderFragment) }
    </Fragment>
;

type RecomposeProps = {
    sentenceFragments: string[];
    renderFragment: FragmentRenderer;
    minutes: string;
    seconds: string;
    milliseconds: string;
    handleChangeMinutes: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeSeconds: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeMilliseconds: React.ChangeEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setMinutes: (s: string) => void;
    _setSeconds: (s: string) => void;
    _setMilliseconds: (s: string) => void;
};

const getTimePartAsString = (n: number, what: 'm' | 's' | 'ms'): string => {
    if (n < 0) {
        return '0';
    }

    switch (what) {
        case 'm':
            return Math.floor(n / 60000).toString();

        case 's':
            return Math.floor((n % 60000) / 1000).toString();

        case 'ms':
            return (n % 1000).toString();
    }
};

const update = ({ onChange, minutes, seconds, milliseconds }: OwnProps & _RecomposeProps) => {
    const n = Math.max(0, Math.min(parseInt(minutes, 10), 1440)) * 60000 +
        Math.max(0, Math.min(parseInt(seconds, 10), 59)) * 1000 +
        Math.max(0, Math.min(parseInt(milliseconds, 10), 999));

    if (!isNaN(n)) {
        onChange(n);
    }
};

const inputStyle = {
    marginLeft: '1em',
    width: '5em',
};

export const DwellTime = compose<RecomposeProps, OwnProps>(
    setDisplayName('DwellTime'),
    translate(),
    withState('minutes', '_setMinutes', ({ value }: OwnProps) => getTimePartAsString(value, 'm')),
    withState('seconds', '_setSeconds', ({ value }: OwnProps) => getTimePartAsString(value, 's')),
    withState('milliseconds', '_setMilliseconds', ({ value }: OwnProps) => getTimePartAsString(value, 'ms')),
    withHandlers({
        handleChangeMinutes: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const minutes = e.target.value;
            props._setMinutes(minutes);
            update({ ...props, minutes });
        },
        handleChangeSeconds: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const seconds = e.target.value;
            props._setSeconds(seconds);
            update({ ...props, seconds });
        },
        handleChangeMilliseconds: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const milliseconds = e.target.value;
            props._setMilliseconds(milliseconds);
            update({ ...props, milliseconds });
        },
    }),
    withProps((props: OwnProps & RecomposeProps & TranslateProps) => ({
        sentenceFragments: makeSentenceFragments(props.t('WC_REPORT_MIN_SEC_MS_SENTENCE')),
        renderFragment: renderFragment({
            M: <Input
                type="number"
                id={ `${props.id}-m` }
                value={ props.minutes }
                onChange={ props.handleChangeMinutes }
                min={ 0 }
                max={ 1440 }
                style={ inputStyle }
            />,
            S: <Input
                type="number"
                id={ `${props.id}-s` }
                value={ props.seconds }
                onChange={ props.handleChangeSeconds }
                min={ 0 }
                max={ 59 }
                style={ inputStyle }
            />,
            MS: <Input
                type="number"
                id={ `${props.id}-ms` }
                value={ props.milliseconds }
                onChange={ props.handleChangeMilliseconds }
                min={ 0 }
                max={ 999 }
                style={ inputStyle }
            />,
        }),
    })),
)(RawDwellTime);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/sensitivity/DwellTime.tsx